import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { activeUsCityExpansionLinks } from "../../../../lib/copy/src/us-city-expansion-links.mjs";

type Admission = { href: string; expiresAt: string };

/** Unactivated, expired, or unreadable inventory never creates homepage links. */
export function useActiveUsCityLinks(enabled = true) {
  const [now, setNow] = useState(Date.now);
  const { data, isError } = useQuery({
    queryKey: ["active-us-city-destinations"],
    enabled: enabled && typeof window !== "undefined",
    queryFn: async ({ signal }): Promise<Admission[]> => {
      const response = await fetch(`${import.meta.env.BASE_URL || "/"}api/landing-pages/us-city-destinations`, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]), cache: "no-store",
      });
      if (!response.ok) throw new Error(`City admission unavailable (${response.status})`);
      const body = await response.json();
      if (!Array.isArray(body.destinations)) throw new Error("Invalid city admission response");
      return body.destinations;
    },
    staleTime: 0, retry: false, refetchInterval: 30_000,
  });
  useEffect(() => {
    const future = (data || []).map(row => Date.parse(row.expiresAt)).filter(time => time > Date.now());
    if (!future.length) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(...future) - Date.now() + 1);
    return () => clearTimeout(timer);
  }, [data, now]);
  return enabled && !isError ? activeUsCityExpansionLinks(data, Math.max(now, Date.now())) : [];
}
